import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import DetailViewHeader from '../../components/JobBoard/Detail/DetailViewHeader';
import DetailView from '../../components/JobBoard/Detail/DetailView';
import DetailSidebar from '../../components/JobBoard/Detail/Sidebar/DetailSidebar';
import ApplyNowModal from '../../components/JobBoard/Apply/ApplyNowModal';
import DetailUnavailable from '../../components/JobBoard/Detail/DetailUnavailable';
import MobileApplyBar from '../../components/JobBoard/Detail/MobileApplyBar';
import MobileJobActionsSheet from '../../components/JobBoard/Detail/MobileJobActionsSheet';
import { getApplyStatus } from '../../api/jobboard/apply.api';
import {
  formatDateToWeekdayString,
  getRemainingTime,
  Toast,
  Banner,
  BaseButton,
  buildJobOptionMeta,
  getJobOptionCode,
  mapJobPostDetailToCardModel,
  useStaleCheck,
  useAuth,
  useIsMobile,
  useLoginRequiredModal,
  trackJobApplyStart,
  useT,
} from '@repo/common';

import {
  bookmarkJobPost,
  getJobPostDetail,
  getJobPostOptions,
  hideJobPostForUser,
  unbookmarkJobPost,
  unhideJobPostForUser,
} from '../../api/jobboard/jobboard.api';

const JobBoardDetail = () => {
  const t = useT();
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef(null);
  const { isLoggedIn } = useAuth();
  const isMobile = useIsMobile();
  const { showLoginModal } = useLoginRequiredModal();

  const [jobDetail, setJobDetail] = useState(null);
  const [optionMeta, setOptionMeta] = useState(null);
  const [remainingTime, setRemainingTime] = useState('');
  const [showFloating, setShowFloating] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // 저장 토스트: 'saved' | 'removed' | null  (Hide 는 토스트 없이 배너로만 알린다)
  const [saveToast, setSaveToast] = useState(null);
  const saveToastTimerRef = useRef(null);
  const [showHiddenBanner, setShowHiddenBanner] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [showActionsSheet, setShowActionsSheet] = useState(false);
  // 지원 여부는 여기서 한 번만 조회하고 사이드바·모바일 바·플로팅 바가 함께 쓴다
  const [hasApplied, setHasApplied] = useState(false);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!jobId) return;
      try {
        setIsLoading(true);
        setHasError(false);
        const [detail, optionGroups] = await Promise.all([
          getJobPostDetail(jobId),
          getJobPostOptions(),
        ]);

        setJobDetail(detail);
        setOptionMeta(buildJobOptionMeta(optionGroups));
        setIsSaved(Boolean(detail.bookmarked));
        // 이미 숨긴 공고로 진입하면 배너를 바로 띄운다
        setIsHidden(Boolean(detail.hiddenByUser));
        setShowHiddenBanner(Boolean(detail.hiddenByUser));
      } catch (error) {
        console.error('Failed to fetch job detail:', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetail();
  }, [jobId]);

  useEffect(() => {
    if (!jobId || !isLoggedIn) {
      setHasApplied(false);
      return;
    }
    getApplyStatus(jobId)
      .then((res) => setHasApplied(Boolean(res?.applied)))
      .catch((error) => console.error('Failed to fetch apply status:', error));
  }, [jobId, isLoggedIn]);

  useStaleCheck(`/job/post/${jobId}`);

  const enrichedJob = useMemo(() => {
    if (!jobDetail || !optionMeta) return jobDetail;

    const cardModel = mapJobPostDetailToCardModel(jobDetail, optionMeta);

    const responseSpeedCode = getJobOptionCode(
      optionMeta,
      jobDetail.responseSpeedOptionItemId,
    );

    return {
      ...jobDetail,
      ...cardModel,
      company: jobDetail.companyName,
      titleChips: responseSpeedCode === 'WITHIN_24H' ? ['quickResponse'] : [],
    };
  }, [jobDetail, optionMeta]);

  useEffect(() => {
    if (!headerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFloating(!entry.isIntersecting);
      },
      { threshold: 0 },
    );

    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, [jobDetail, optionMeta]);

  const isOngoingHiring = jobDetail?.hiringStatus === 'ONGOING_HIRING';

  // 공고마감 / 숨김처리 / 삭제 상태이면 unavailable 화면 노출
  // - CLOSED: 마감 처리됨
  // - HIDDEN: 운영자가 비공개로 전환
  // - DRAFT: 미공개 초안
  // - DELETED_FROM_*: 삭제됨
  // - hiringStatus !== ONGOING_HIRING && applicationDeadline 지남: 마감 자동 처리
  const UNAVAILABLE_STATUSES = new Set([
    'CLOSED',
    'HIDDEN',
    'DRAFT',
    'DELETED_FROM_ACTIVE',
    'DELETED_FROM_HIDDEN',
    'DELETED_FROM_DRAFT',
    'DELETED_FROM_CLOSED',
  ]);
  const isStatusUnavailable =
    !!jobDetail?.status && UNAVAILABLE_STATUSES.has(jobDetail.status);
  const isDeadlineExpired =
    !isOngoingHiring &&
    !!jobDetail?.applicationDeadline &&
    new Date(jobDetail.applicationDeadline) < new Date();
  const isUnavailable = isStatusUnavailable || isDeadlineExpired;

  useEffect(() => {
    if (isOngoingHiring || !jobDetail?.applicationDeadline) return;
    const update = () => {
      setRemainingTime(getRemainingTime(jobDetail.applicationDeadline));
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [jobDetail?.applicationDeadline, isOngoingHiring]);

  const getApplySource = () => {
    return location.state?.source || 'job_board';
  };

  const handleApplyClick = () => {
    if (!isLoggedIn) {
      showLoginModal('jobboard_detail_floating_apply_cta');
      return;
    }
    // isUnavailable 이면 이 화면 자체가 DetailUnavailable 로 대체되지만 방어적으로 한 번 더 막는다
    if (hasApplied) return;
    if (!isUnavailable) {
      trackJobApplyStart({
        job_id: String(enrichedJob.id),
        job_title: enrichedJob.title,
        company: enrichedJob.companyName || enrichedJob.company,
        e7_support: Boolean(enrichedJob.hasE7Support),
        source: getApplySource(),
      });
      setShowApplyModal(true);
    }
  };

  const clearSaveToast = () => {
    if (saveToastTimerRef.current) clearTimeout(saveToastTimerRef.current);
    setSaveToast(null);
  };

  const showSaveToast = (kind) => {
    if (saveToastTimerRef.current) clearTimeout(saveToastTimerRef.current);
    setSaveToast(null);
    // 같은 토스트를 연속으로 띄워도 다시 보이도록 한 틱 뒤에 켠다
    saveToastTimerRef.current = setTimeout(() => {
      setSaveToast(kind);
      saveToastTimerRef.current = setTimeout(() => setSaveToast(null), 3300);
    }, 10);
  };

  useEffect(
    () => () => {
      if (saveToastTimerRef.current) clearTimeout(saveToastTimerRef.current);
    },
    [],
  );

  /**
   * 숨기기/해제.
   * 피드백은 상단 배너의 유무로만 표현한다 — 토스트를 띄우지 않고,
   * 떠 있는 저장 토스트가 있으면 함께 정리한다.
   */
  const toggleHidden = async (nextHidden) => {
    setIsHidden(nextHidden);
    setShowHiddenBanner(nextHidden);
    clearSaveToast();
    try {
      await handleToggleHide(nextHidden);
    } catch (error) {
      console.error('Hide toggle failed:', error);
      setIsHidden(!nextHidden);
      setShowHiddenBanner(!nextHidden);
    }
  };

  const toggleSaved = async (nextSaved) => {
    setIsSaved(nextSaved);
    try {
      await handleToggleSave(nextSaved);
      showSaveToast(nextSaved ? 'saved' : 'removed');
    } catch (error) {
      console.error('Save toggle failed:', error);
      setIsSaved(!nextSaved);
    }
  };

  const handleToggleHide = async (nextHidden) => {
    if (!jobDetail) return;
    if (nextHidden) {
      await hideJobPostForUser(jobDetail.id);
      setJobDetail((prev) => ({ ...prev, hiddenByUser: true }));
      return;
    }
    await unhideJobPostForUser(jobDetail.id);
    setJobDetail((prev) => ({ ...prev, hiddenByUser: false }));
  };

  const handleToggleSave = async (nextSaved) => {
    if (!jobDetail) return;
    if (nextSaved) {
      await bookmarkJobPost(jobDetail.id);
      setJobDetail((prev) => ({ ...prev, bookmarked: true }));
      return;
    }
    await unbookmarkJobPost(jobDetail.id);
    setJobDetail((prev) => ({ ...prev, bookmarked: false }));
  };

  if (isLoading) {
    return (
      <div className='w-full min-h-screen overflow-y-scroll bg-grayscale-gray01 flex flex-col items-center px-4 tabletH:px-6 tabletV:px-[80px] pt-6 tabletV:pt-[60px] pb-[120px] tabletV:pb-[240px]'>
        <div className='typo-b18Strong text-text-secondary'>
          {t('Loading job detail...', '공고 정보를 불러오는 중...')}
        </div>
      </div>
    );
  }

  if (hasError || !enrichedJob || isUnavailable) {
    return <DetailUnavailable />;
  }

  return (
    <div className='w-full min-h-screen overflow-y-scroll bg-grayscale-gray01 flex flex-col items-center px-4 tabletH:px-6 tabletV:px-[80px] pt-6 tabletV:pt-[60px] pb-[100px] tabletV:pb-[240px]'>
      {showHiddenBanner && (
        <Banner
          type='success'
          size='m'
          label={t('Job hidden', '공고를 숨겼습니다')}
          message={t(
            'This job won’t appear again in your feed.',
            '이 공고는 목록에 다시 표시되지 않습니다.',
          )}
          className='mb-6 w-full rounded-[15px] max-w-[1017px]'
          buttonLabel={t('Undo', '실행 취소')}
          onButtonClick={() => toggleHidden(false)}
          buttonLabelSecond={
            isMobile ? undefined : t('Go to jobs', '공고 목록으로')
          }
          onSecondButtonClick={() => navigate('/jobs')}
          onClose={() => setShowHiddenBanner(false)}
        />
      )}
      <div className='w-full flex flex-col tabletV:flex-row gap-4 tabletH:gap-6 justify-center min-h-screen'>
        <div className='contents tabletV:block tabletV:w-auto'>
          <div ref={headerRef} className='order-1 w-full min-w-0'>
            <DetailViewHeader
              job={enrichedJob}
              optionMeta={optionMeta}
              onOpenActions={() => setShowActionsSheet(true)}
            />
          </div>
          <div className='order-3 w-full min-w-0'>
            <DetailView job={enrichedJob} optionMeta={optionMeta} />
          </div>
        </div>
        <DetailSidebar
          className='order-2 border-b border-grayscale-gray02 pb-[30px] tabletV:order-2 tabletV:border-b-0 tabletV:pb-0'
          job={enrichedJob}
          optionMeta={optionMeta}
          isSaved={isSaved}
          isHidden={isHidden}
          hasApplied={hasApplied}
          onApplied={() => setHasApplied(true)}
          onToggleSave={toggleSaved}
          onToggleHide={toggleHidden}
        />
      </div>

      {saveToast && (
        <div className='fixed bottom-[100px] tabletV:bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
          <Toast
            type='success'
            state='white'
            title={
              saveToast === 'saved'
                ? t('Saved to your job', '공고를 저장했습니다')
                : t('Removed from Saved Job', '저장한 공고에서 삭제했습니다')
            }
            direction='up'
            buttonLabel={
              saveToast === 'saved'
                ? t('View Saved Jobs', '저장한 공고 보기')
                : undefined
            }
            onButtonClick={
              saveToast === 'saved'
                ? () => navigate('/mypage?tab=My%20Apply&applyTab=saved')
                : undefined
            }
            onClose={() => setSaveToast(null)}
            customWidth={saveToast === 'saved' ? '380px' : '320px'}
          />
        </div>
      )}

      {showActionsSheet && (
        <MobileJobActionsSheet
          isSaved={isSaved}
          isHidden={isHidden}
          onClose={() => setShowActionsSheet(false)}
          onToggleSave={() => toggleSaved(!isSaved)}
          onToggleHide={() => toggleHidden(!isHidden)}
        />
      )}

      <MobileApplyBar
        job={enrichedJob}
        isSaved={isSaved}
        isHidden={isHidden}
        hasApplied={hasApplied}
        onApplied={() => setHasApplied(true)}
        onToggleSave={toggleSaved}
      />

      {showFloating && (
        <div className='hidden tabletV:flex fixed bottom-0 left-1/2 -translate-x-1/2 z-[999] flex-col gap-2.5 px-[80px] py-6'>
          <div
            className='flex justify-between bg-grayscale-gray00 border-grayscale-gray02 py-4 px-6 rounded-[15px] 
                        shadow-popup-02 w-[1166px]'
          >
            <div>
              <div className='typo-b12Strong flex gap-2'>
                <div className='text-text-secondary'>
                  {isOngoingHiring
                    ? t('Hiring status', '채용 상태')
                    : t('Application deadline', '지원 마감일')}
                </div>
                <div className='w-px self-stretch relative bg-grayscale-gray02' />
                <div className='text-text-accent'>
                  {isOngoingHiring
                    ? t('Ongoing hiring', '상시 채용')
                    : formatDateToWeekdayString(
                        enrichedJob.applicationDeadline,
                      )}
                </div>
              </div>
              <div className='typo-title1'>{enrichedJob.title}</div>
            </div>

            <div className='flex items-center gap-2 self-stretch'>
              {!isOngoingHiring && (
                <div className='typo-b18Strong px-4 py-2 text-text-accent bg-functional-accent01 rounded-lg'>
                  {remainingTime}
                </div>
              )}
              <BaseButton
                size='l'
                hideIcon
                width='180px'
                onClick={handleApplyClick}
                isDisabled={hasApplied}
              >
                {hasApplied
                  ? t('Applied', '지원 완료')
                  : t('Apply now', '지원하기')}
              </BaseButton>
            </div>

            {showApplyModal && (
              <ApplyNowModal
                job={enrichedJob}
                isOpen={showApplyModal}
                onClose={() => setShowApplyModal(false)}
                onSuccess={() => setHasApplied(true)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JobBoardDetail;
